import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Dropdown from '../../components/ui/Dropdown';
import Icon from '../../components/AppIcon';
import {
  Sparkles,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle2,
  HelpCircle,
  Send,
  Building2,
  Headset
} from 'lucide-react';


// ===== ENHANCED ACCORDION COMPONENT =====
const AccordionItem = ({ question, answer, isOpen, onClick, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="group"
    >
      <motion.div
        layout
        className="bg-white shadow-sm hover:shadow-md rounded-2xl overflow-hidden border border-gray-100 transition-all duration-300"
        whileHover={{ y: -2 }}
      >
        <button
          onClick={onClick}
          className="w-full flex items-center justify-between p-6 md:p-7 text-left hover:bg-slate-50 transition-colors duration-200"
          aria-expanded={isOpen}
        >
          <div className="flex items-start gap-4 flex-1">
            <motion.div
              animate={{
                scale: isOpen ? 1.1 : 1,
                rotate: isOpen ? 360 : 0
              }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center"
            >
              <HelpCircle className="w-5 h-5 text-blue-600" strokeWidth={2.5} />
            </motion.div>


            <h3 className="text-lg font-bold text-slate-900 pr-4 leading-tight">
              {question}
            </h3>
          </div>


          <motion.div
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center"
          >
            <motion.svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-white"
            >
              <motion.path
                d="M9 3V15"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <motion.path
                d="M3 9H15"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </motion.svg>
          </motion.div>
        </button>


        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{
                height: "auto",
                opacity: 1,
                transition: {
                  height: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                  opacity: { duration: 0.3, delay: 0.1 }
                }
              }}
              exit={{
                height: 0,
                opacity: 0,
                transition: {
                  height: { duration: 0.3, ease: "easeIn" },
                  opacity: { duration: 0.2 }
                }
              }}
              className="overflow-hidden border-t border-gray-100"
            >
              <div className="px-6 md:px-7 py-6 bg-slate-50">
                <p className="text-slate-600 leading-relaxed text-[15px]" style={{ lineHeight: '1.7' }}>
                  {answer}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};


const ContactPage = () => {
  const heroRef = useRef(null);
  const footerRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();


  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceInterest: '',
    description: '',
    preferredContact: 'email',
    honeypot: ''
  });


  const [errors, setErrors] = useState({});
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);


  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };


  // Service categories matching the services page
  const serviceOptions = [
    { value: 'development', label: 'Development' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'security', label: 'Security' },
    { value: 'data', label: 'Data & AI' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'design', label: 'Design' },
    { value: 'support', label: 'Support' }
  ];


  const contactMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'either', label: 'Either' }
  ];


  // ===== FAQ DATA =====
  const faqData = [
    {
      question: "How soon can you start on my project?",
      answer: "Our typical onboarding process takes 1-2 weeks, depending on the project scope and our current workload. For urgent requests, we offer expedited services at an additional fee."
    },
    {
      question: "Do you offer ongoing maintenance services?",
      answer: "Yes, we provide both project-based work and ongoing maintenance services. Our maintenance packages include regular updates, security patches, performance monitoring, and technical support."
    },
    {
      question: "How do you handle project revisions?",
      answer: "Our process includes two rounds of revisions at no additional cost. Beyond that, we work with you to determine if adjustments are within the original scope or require additional resources."
    },
    {
      question: "What information should I prepare before our first meeting?",
      answer: "To make our initial consultation most productive, please prepare your project goals, target audience, desired features, examples of sites/apps you like, and any specific requirements or constraints such as timeline or budget limitations."
    }
  ];


  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };


  // Load saved form data from localStorage on component mount
  useEffect(() => {
    const savedFormData = localStorage.getItem('contactFormData');
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        setFormData(prevData => ({
          ...prevData,
          ...parsedData
        }));


        if (parsedData.description) {
          setCharCount(parsedData.description.length);
        }
      } catch (error) {
        console.error('Error parsing saved form data:', error);
        localStorage.removeItem('contactFormData');
      }
    }
  }, []);


  useEffect(() => {
    const isFormEmpty = (
      !formData.name &&
      !formData.email &&
      !formData.phone &&
      !formData.company &&
      !formData.serviceInterest &&
      !formData.description
    );


    if (!isFormEmpty && !submitSuccess) {
      const { honeypot, ...dataToSave } = formData;
      localStorage.setItem('contactFormData', JSON.stringify(dataToSave));
    }
  }, [formData, submitSuccess]);


  const validateField = (name, value) => {
    let fieldError = '';


    switch (name) {
      case 'name':
        if (!value.trim()) {
          fieldError = 'Name is required';
        }
        break;
      case 'email':
        if (!value.trim()) {
          fieldError = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          fieldError = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (value && !/^[\d\+\-\(\)\s]{7,20}$/.test(value)) {
          fieldError = 'Please enter a valid phone number';
        }
        break;
      case 'description':
        if (!value.trim()) {
          fieldError = 'Project description is required';
        } else if (value.trim().length < 20) {
          fieldError = 'Please provide more details (at least 20 characters)';
        }
        break;
      default:
        break;
    }


    return fieldError;
  };


  const handleChange = (e) => {
    const { name, value } = e.target;


    setFormData(prev => ({
      ...prev,
      [name]: value
    }));


    if (name === 'description') {
      setCharCount(value.length);
    }


    const fieldError = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));
  };


  const handleSelectChange = (field, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [field]: selectedOption?.value || null
    }));
  };


  const validateForm = () => {
    const newErrors = {};


    newErrors.name = validateField('name', formData.name);
    newErrors.email = validateField('email', formData.email);
    newErrors.phone = validateField('phone', formData.phone);
    newErrors.description = validateField('description', formData.description);


    setErrors(newErrors);


    return !Object.values(newErrors).some(error => error !== '');
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot trap
    if (formData.honeypot) {
      setIsSubmitting(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitSuccess(true);
      }, 1000);
      return;
    }

    // Validate before submitting
    const isValid = validateForm();
    if (!isValid) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Prepare Netlify encoded form payload
      const encoded = new URLSearchParams({
        "form-name": "contact",
        ...formData,
      }).toString();

      // Submit to Netlify
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encoded,
      });

      // Success UI flow (unchanged)
      setSubmitSuccess(true);
      localStorage.removeItem('contactFormData');

      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        serviceInterest: null,
        description: '',
        preferredContact: 'email',
        honeypot: ''
      });

      setCharCount(0);

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('There was an error submitting your request. Please try again.');
      setRetryCount(prev => prev + 1);
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleRetry = () => {
    setSubmitError('');
    handleSubmit(new Event('submit'));
  };


  const handleNewForm = () => {
    setSubmitSuccess(false);
    window.scrollTo(0, 0);
  };


  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 mt-10" ref={containerRef}>
      <Header />


      <main className="flex-grow" id="main-content">
        {/* Enhanced Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white py-20 3xl:py-32 overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl 3xl:max-w-5xl 4xl:max-w-6xl mx-auto text-center"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-5 py-2.5 3xl:px-6 3xl:py-3 rounded-full mb-6 3xl:mb-8 border border-white/20"
              >
                <MessageSquare className="w-5 h-5 3xl:w-6 3xl:h-6 text-yellow-300" />
                <span className="text-sm 3xl:text-base font-bold uppercase tracking-wider">GET IN TOUCH</span>
              </motion.div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl 3xl:text-7xl font-bold mb-6 3xl:mb-8 leading-tight tracking-tight">
                Get in Touch
              </h1>
              <p className="text-lg md:text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl opacity-95 leading-relaxed max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto" style={{ lineHeight: '1.8' }}>
                Have a project in mind or want to learn more about our services? We'd love to hear from you. Fill out the form below and we'll get back to you shortly.
              </p>
            </motion.div>
          </div>
        </section>


        {/* ENHANCED CONTACT FORM SECTION */}
        <section className="py-16 md:py-20 3xl:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 3xl:px-36 4xl:px-50 5xl:px-60 max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            <div className="max-w-6xl 3xl:max-w-7xl 4xl:max-w-[2000px] mx-auto">
              {submitSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-3xl shadow-xl p-12 text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-50 rounded-full flex items-center justify-center mx-auto mb-6"
                  >
                    <CheckCircle2 className="w-10 h-10 text-green-600" strokeWidth={2.5} />
                  </motion.div>
                  <h2 className="text-2xl md:text-3xl 3xl:text-4xl 4xl:text-5xl 5xl:text-6xl 6xl:text-7xl font-bold text-slate-900 mb-4">Thank You for Contacting Us!</h2>
                  <p className="text-slate-600 mb-8 max-w-lg mx-auto text-lg leading-relaxed">
                    Your message has been received. We'll review your inquiry and get back to you within 24-48 business hours.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleNewForm}
                      >
                        Submit Another Request
                      </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        variant="secondary"
                        size="lg"
                        onClick={() => navigate('/services-page')}
                      >
                        Browse Our Services
                      </Button>
                    </motion.div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="bg-white rounded-3xl shadow-xl overflow-hidden"
                >
                  <div className="md:flex">
                    {/* ENHANCED CONTACT FORM */}
                    <div className="md:w-2/3 p-8 md:p-10 lg:p-12">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                          <Send className="w-6 h-6 text-white" strokeWidth={2.5} />
                        </div>
                        <h2 className="text-3xl font-bold text-slate-900">Get in Touch</h2>
                      </div>


                      <p className="text-slate-600 mb-10 text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl leading-relaxed">
                        Please fill out the form below with your project details and we'll get back to you as soon as possible.
                      </p>


                      <form
                        onSubmit={handleSubmit}
                        className="space-y-6"
                        name="contact"
                        method="POST"
                        data-netlify="true"
                        data-netlify-honeypot="honeypot"
                      >
                        <input type="hidden" name="form-name" value="contact" />
                        {/* Honeypot field */}
                        <div className="opacity-0 absolute top-0 left-0 h-0">
                          <label htmlFor="honeypot">Leave this field empty:</label>
                          <input
                            type="text"
                            id="honeypot"
                            name="honeypot"
                            value={formData.honeypot}
                            onChange={handleChange}
                            tabIndex="-1"
                            autoComplete="off"
                          />
                        </div>


                        {/* Name Field */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 }}
                        >
                          <Input
                            label="Full Name"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your name"
                            required
                            error={errors.name}
                            icon="User"
                            aria-label="Full Name"
                            aria-required="true"
                          />
                        </motion.div>


                        {/* Email & Phone Fields */}
                        <motion.div
                          className="grid md:grid-cols-2 gap-6"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          <Input
                            label="Email Address"
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your.email@example.com"
                            required
                            error={errors.email}
                            icon="Mail"
                            aria-label="Email Address"
                            aria-required="true"
                          />


                          <Input
                            label="Phone Number (Optional)"
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+91 1234567890"
                            error={errors.phone}
                            icon="Phone"
                            aria-label="Phone Number"
                          />
                        </motion.div>


                        {/* Company & Service Interest Fields */}
                        <motion.div
                          className="grid md:grid-cols-2 gap-6"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <Input
                            label="Company Name (Optional)"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your company"
                            icon="Building"
                            aria-label="Company Name"
                          />


                          <Dropdown
                            label="Service of Interest"
                            variant="selection"
                            placeholder="Select a service"
                            items={serviceOptions}
                            value={formData.serviceInterest}
                            onChange={(option) => handleSelectChange('serviceInterest', option.value)}
                            aria-label="Service of Interest"
                          />


                        </motion.div>


                        {/* Project Description Field */}
                        <motion.div
                          className="space-y-2"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          <label
                            htmlFor="description"
                            className="block text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-medium text-slate-700"
                          >
                            Project Description
                            <span className="text-error ml-1">*</span>
                          </label>


                          <div className="relative">
                            <textarea
                              id="description"
                              name="description"
                              rows="5"
                              value={formData.description}
                              onChange={handleChange}
                              placeholder="Please describe your project, requirements, and timeline..."
                              className={`w-full rounded-xl border px-4 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all ${errors.description ? 'border-error focus:border-error focus:ring-error/30' : 'border-slate-200'}`}
                              aria-label="Project Description"
                              aria-required="true"
                              aria-invalid={errors.description ? 'true' : 'false'}
                            ></textarea>


                            <div className="flex justify-between mt-2 text-xs">
                              {errors.description ? (
                                <span className="text-error font-medium">{errors.description}</span>
                              ) : (
                                <span className="invisible">Hidden placeholder</span>
                              )}
                              <span className={`font-medium ${charCount > 500 ? 'text-warning' : 'text-slate-500'}`}>
                                {charCount}/2000
                              </span>
                            </div>
                          </div>
                        </motion.div>


                        {/* Budget Range Field - HIDDEN */}
                        {/* This section is commented out to hide the budget range field */}


                        {/* Error Message */}
                        <AnimatePresence>
                          {submitError && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="p-5 bg-red-50 border-2 border-red-200 rounded-xl"
                            >
                              <div className="flex items-start">
                                <Icon name="AlertTriangle" className="text-red-600 mr-3 mt-0.5" size={20} />
                                <div className="flex-1">
                                  <p className="text-red-700 font-semibold mb-1">{submitError}</p>
                                  <p className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-red-600">
                                    Please try again or contact us directly at contact@zovox.in
                                  </p>
                                  {retryCount < 3 && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="mt-3"
                                      onClick={handleRetry}
                                    >
                                      Retry Submission
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>


                        {/* Submit Button */}
                        <motion.div
                          className="pt-4"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.6 }}
                        >
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Button
                              type="submit"
                              variant="primary"
                              size="lg"
                              className="w-full md:w-auto px-10 shadow-lg hover:shadow-xl"
                              isLoading={isSubmitting}
                              disabled={isSubmitting}
                              icon={isSubmitting ? null : "Send"}
                              iconPosition="right"
                            >
                              {isSubmitting ? 'Submitting...' : 'Submit Request'}
                            </Button>
                          </motion.div>
                        </motion.div>
                      </form>
                    </div>


                    {/* ENHANCED CONTACT INFORMATION SIDEBAR */}
                    <div className="md:w-1/3 bg-gradient-to-br from-blue-50 to-purple-50 p-8 md:p-10 lg:p-12">
                      <h3 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                        <Building2 className="w-6 h-6 text-blue-600" />
                        Contact Info
                      </h3>


                      <div className="space-y-8">
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                              <Clock className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-2">Business Hours</h4>
                              <p className="text-sm text-slate-600 leading-relaxed">Monday - Friday: 9:00 AM - 6:00 PM IST</p>
                              <p className="text-sm text-slate-600">Saturday - Sunday: Closed</p>
                            </div>
                          </div>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                              <Mail className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-2">Email</h4>
                              <a
                                href="mailto:contact@zovox.in"
                                className="text-blue-600 hover:text-blue-700 font-medium text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl transition-colors break-all"
                              >
                                contact@zovox.in
                              </a>
                            </div>
                          </div>
                        </motion.div>


                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                              <MapPin className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-2">Address</h4>
                              <address className="not-italic text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-slate-600 leading-relaxed mb-4">
                                <p>Chandigarh, India</p>
                              </address>


                              {/* Enhanced Map - Updated for Chandigarh */}
                              <div className="w-full h-48 bg-slate-200 rounded-xl overflow-hidden shadow-inner">
                                <iframe
                                  title="Office Location Map"
                                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d109740.02712922098!2d76.69348655820312!3d30.741482!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390fed0be66ec96b%3A0xa5ff67f9527319fe!2sChandigarh!5e0!3m2!1sen!2sin!4v1732822800000!5m2!1sen!2sin"
                                  width="100%"
                                  height="100%"
                                  style={{ border: 0 }}
                                  allowFullScreen=""
                                  loading="lazy"
                                  referrerPolicy="no-referrer-when-downgrade"
                                  className="grayscale hover:grayscale-0 transition-all duration-300"
                                  aria-label="Map showing our office location in Chandigarh"
                                ></iframe>
                              </div>
                            </div>
                          </div>
                        </motion.div>


                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.6 }}
                          className="pt-4 border-t border-slate-200"
                        >
                          <h4 className="text-sm font-bold text-slate-900 mb-4">Connect With Us</h4>
                          <div className="flex gap-3">
                            {[
                              { name: 'Twitter', icon: 'Twitter', url: 'https://twitter.com' },
                              { name: 'LinkedIn', icon: 'Linkedin', url: 'https://www.linkedin.com/company/zovox-technologies' },
                              { name: 'Facebook', icon: 'Facebook', url: 'https://facebook.com' },
                              { name: 'Instagram', icon: 'Instagram', url: 'https://instagram.com' }
                            ].map((social) => (
                              <motion.a
                                key={social.name}
                                href={social.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={social.name}
                                whileHover={{ scale: 1.1, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-slate-600 hover:text-blue-600 hover:shadow-md transition-all"
                              >
                                <Icon name={social.icon} size={18} />
                              </motion.a>
                            ))}
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </section>


        {/* ENHANCED FAQ SECTION */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="max-w-4xl mx-auto text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-6">
                <HelpCircle className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-bold text-blue-600 uppercase tracking-wider">FAQ</span>
              </div>


              <h2 className="text-3xl md:text-4xl 3xl:text-5xl font-bold text-center mb-12 3xl:mb-16 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-tight tracking-tight py-1">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Find answers to common questions about our services and process
              </p>
            </motion.div>


            <div className="max-w-3xl mx-auto space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem
                  key={index}
                  index={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFaqIndex === index}
                  onClick={() => toggleFaq(index)}
                />
              ))}
            </div>
          </div>
        </section>
      </main>


      <Footer ref={footerRef} />
    </div>
  );
};


export default ContactPage;
